import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as globals from '../../../../components/GlobalScripts.js';

function uniq(arr) {
    return Array.from(new Set((arr || []).filter(Boolean)));
}

function parseKeylogins(text) {
    const raw = String(text || '')
        .split(/[\n,]+/g)
        .map((s) => s.trim())
        .filter(Boolean);

    return uniq(raw);
}

function getAccName(acc) {
    const nombre = `${acc?.account?.nombre || ''} ${acc?.account?.apellidos || ''}`.trim();
    return nombre || acc?.keylogin || '—';
}

export default function CuponAssignModal({ open, coupon, onClose, onAssign }) {
    const path_api = localStorage.getItem('path_api');

    const apiBasePayload = useMemo(() => ({
        rute: globals.getRuteBackend(),
        session_id: globals.get_session_id(),
        keylogin: globals.get_keylogin(),
    }), []);

    const [holdersQ, setHoldersQ] = useState('');
    const [holders, setHolders] = useState([]);
    const [holdersPage, setHoldersPage] = useState(1);
    const [holdersTotalPages, setHoldersTotalPages] = useState(1);
    const [holdersTotal, setHoldersTotal] = useState(0);
    const holdersPerPage = 8;
    const [holdersLoading, setHoldersLoading] = useState(false);

    const [holdersIndex, setHoldersIndex] = useState({});

    const [search, setSearch] = useState('');
    const [clients, setClients] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 8;
    const [loading, setLoading] = useState(false);

    const [selected, setSelected] = useState([]);
    const [nota, setNota] = useState('');

    const [showRaw, setShowRaw] = useState(false);
    const [keyloginsTxt, setKeyloginsTxt] = useState('');

    useEffect(() => {
        if (!open) return;

        setSearch('');
        setClients([]);
        setPage(1);
        setTotalPages(1);
        setSelected([]);
        setNota('');
        setShowRaw(false);
        setKeyloginsTxt('');

        setHoldersQ('');
        setHolders([]);
        setHoldersPage(1);
        setHoldersTotalPages(1);
        setHoldersTotal(0);
        setHoldersLoading(false);
        setHoldersIndex({});
    }, [open, coupon?.id]);

    const selectedKeylogins = useMemo(() => uniq(selected.map((s) => s.keylogin)), [selected]);
    const count = selectedKeylogins.length;

    const assignedKeyloginsSet = useMemo(() => {
        return new Set(Object.keys(holdersIndex || {}));
    }, [holdersIndex]);

    const close = () => onClose?.();

    const fetchHolders = async ({ nextPage = 1, nextQ = holdersQ } = {}) => {
        if (!coupon?.id) return;

        setHoldersLoading(true);
        globals.openViewCharge();

        try {
            const res = await axios.post(`${path_api}/admin/cupones/holders.php`, {
                ...apiBasePayload,
                cupon_id: Number(coupon.id),
                page: nextPage,
                itemsPerPage: holdersPerPage,
                q: String(nextQ || '').trim(),
            });

            if (res.data?.mensaje === 'success') {
                const items = Array.isArray(res.data?.items) ? res.data.items : [];
                const paging = res.data?.paging || {};

                setHolders(items);
                setHoldersTotal(Number(paging?.total || 0));
                setHoldersTotalPages(Number(paging?.total_pages || 1));
                setHoldersPage(Number(paging?.page || nextPage));

                setHoldersIndex((prev) => {
                    const next = { ...(prev || {}) };
                    items.forEach((it) => {
                        const key = String(it?.keylogin || '').trim();
                        if (!key) return;
                        next[key] = {
                            activo: Number(it?.activo || 0),
                            nombre: `${it?.nombre || ''} ${it?.apellidos || ''}`.trim() || key,
                            email: it?.email || '',
                        };
                    });
                    return next;
                });

                return;
            }

            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: res.data?.mensaje || "No se obtuvieron los perfiles con este cupón",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4000
            });
        } catch {
            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: "Error al cargar los perfiles con este cupón",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4000
            });
        } finally {
            globals.closeViewCharge();
            setHoldersLoading(false);
        }
    };

    const handleUnassignFromHolder = async (holder) => {
        if (!coupon?.id) return;

        const cupon_id = Number(coupon.id);
        const cliente_keylogin = String(holder?.keylogin || '').trim();
        if (!cupon_id || !cliente_keylogin) return;

        const code = coupon?.codigo || `#${cupon_id}`;
        const ok = window.confirm(
            `¿Remover el cupón ${code} del cliente ${holder.nombre + ' ' + holder.apellidos}?`
        );
        if (!ok) return;

        globals.openViewCharge();
        try {
            const res = await axios.post(`${path_api}/admin/cupones/unassign.php`, {
                ...apiBasePayload,
                cupon_id,
                cliente_keylogin,
            });

            if (res.data?.mensaje === 'success') {
                globals.createAlert({
                    type: "success",
                    msg_title: "Listo",
                    msg: "Cupón removido del cliente.",
                    effect: "opacidad 2s, desplazamiento 2s",
                    time_life: 4200
                });

                setSelected((prev) => prev.filter((x) => x.keylogin !== cliente_keylogin));

                await fetchHolders({ nextPage: holdersPage, nextQ: holdersQ });

                setHoldersIndex((prev) => {
                    const next = { ...(prev || {}) };
                    delete next[cliente_keylogin];
                    return next;
                });

                return;
            }

            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: res.data?.mensaje || "No se pudo remover el cupón.",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4200
            });
        } catch {
            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: "Error al remover el cupón.",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4200
            });
        } finally {
            globals.closeViewCharge();
        }
    };

    useEffect(() => {
        if (!open) return;
        if (!coupon?.id) return;
        fetchHolders({ nextPage: 1, nextQ: '' });
    }, [open, coupon?.id]);

    const fetchClients = async ({ nextPage = 1, nextSearch = search } = {}) => {
        setLoading(true);
        globals.openViewCharge();

        try {
            const res = await axios.post(`${path_api}/update-clients/get_clients.php`, {
                ...apiBasePayload,
                page: nextPage,
                itemsPerPage,
                search: nextSearch,
            });

            if (res.data?.mensaje === 'success') {
                setClients(Array.isArray(res.data?.accounts) ? res.data.accounts : []);
                setTotalPages(Number(res.data?.totalPages || 1));
                setPage(nextPage);
                return;
            }

            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: res.data?.mensaje || "No se obtuvieron los clientes",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4000
            });
        } catch {
            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: "Error al cargar los clientes",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4000
            });
        } finally {
            globals.closeViewCharge();
            setLoading(false);
        }
    };

    const addClient = (acc) => {
        const keylogin = acc?.keylogin;
        if (!keylogin) return;

        if (assignedKeyloginsSet.has(String(keylogin))) {
            globals.createAlert({
                type: "error",
                msg_title: "Aviso",
                msg: "Este cliente ya tiene este cupón (activo o inactivo). Si quieres reasignar, primero remuévelo de la lista de arriba.",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 4200
            });
            return;
        }

        const nombre = getAccName(acc);
        const email = acc?.email || '';

        setSelected((prev) => {
            if (prev.some((x) => x.keylogin === keylogin)) return prev;
            return [...prev, { keylogin, nombre: nombre || keylogin, email }];
        });
    };

    const removeClient = (keylogin) => {
        setSelected((prev) => prev.filter((x) => x.keylogin !== keylogin));
    };

    const clearSelected = () => setSelected([]);

    const mergeRawKeylogins = () => {
        const extra = parseKeylogins(keyloginsTxt);
        if (!extra.length) return;

        const filtered = extra.filter((kl) => !assignedKeyloginsSet.has(String(kl)));

        if (filtered.length !== extra.length) {
            globals.createAlert({
                type: "info",
                msg_title: "Nota",
                msg: "Algunos keylogins ya tenían el cupón (activo o inactivo) y se omitieron.",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 3500
            });
        }

        setSelected((prev) => {
            const current = new Map(prev.map((x) => [x.keylogin, x]));
            filtered.forEach((kl) => {
                if (!current.has(kl)) current.set(kl, { keylogin: kl, nombre: kl, email: '' });
            });
            return Array.from(current.values());
        });

        setKeyloginsTxt('');
        setShowRaw(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!coupon?.id) return;

        if (!selectedKeylogins.length) {
            globals.createAlert({
                type: "error",
                msg_title: "Error",
                msg: "Selecciona al menos un cliente para asignar.",
                effect: "opacidad 2s, desplazamiento 2s",
                time_life: 3500
            });
            return;
        }

        await onAssign?.({
            cupon_id: coupon.id,
            keylogins: selectedKeylogins,
            nota: String(nota || '').trim(),
        });

        await fetchHolders({ nextPage: 1, nextQ: holdersQ });
        setSelected([]);
        setNota('');
    };

    const alreadySelected = (keylogin) => selectedKeylogins.includes(keylogin);

    const alreadyHasCoupon = (keylogin) => assignedKeyloginsSet.has(String(keylogin));

    if (!open) return null;

    return (
        <div className="cupon-modal__overlay" onMouseDown={close}>
            <div className="cupon-modal cupon-modal--wide" onMouseDown={(e) => e.stopPropagation()}>
                <div className="cupon-modal__head">
                    <div className="cupon-modal__title">Asignar cupón</div>

                    <button className="cupon-iconbtn" type="button" onClick={close} title="Cerrar">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <div className="cupon-modal__body">
                    <div className="cupon-assign__coupon">
                        <div className="cupon-badge cupon-badge--dark">Cupón</div>
                        <div className="cupon-assign__code">{coupon?.codigo}</div>
                        <div className="cupon-assign__title">{coupon?.titulo || '—'}</div>
                    </div>

                    <div className="cupon-assign__panel" style={{ marginBottom: 12 }}>
                        <div className="cupon-assign__panelhead" style={{ justifyContent: 'space-between' }}>
                            <div>
                                <div className="cupon-assign__paneltitle">
                                    Ya asignado a <b>{holdersTotal}</b> clientes
                                </div>
                                <div className="cupon-hint">Un cupón sin clientes asignados será un cupón “público”.</div>
                            </div>

                            <button
                                className="cupon-btn cupon-btn--ghost"
                                type="button"
                                onClick={() => fetchHolders({ nextPage: 1, nextQ: holdersQ })}
                                disabled={holdersLoading}
                                title="Actualizar"
                            >
                                Actualizar
                            </button>
                        </div>

                        <div className="cupon-assign__results" style={{ marginTop: 10 }}>
                            {holdersLoading && <div className="cupon-assign__empty">Cargando...</div>}

                            {!holdersLoading && holders.length === 0 && (
                                <div className="cupon-assign__empty">
                                    Nadie tiene este cupón asignado.
                                </div>
                            )}

                            {!holdersLoading && holders.map((h) => {
                                const name = `${h?.nombre || ''} ${h?.apellidos || ''}`.trim() || h?.keylogin;
                                const status = Number(h?.activo || 0) === 1 ? 'Activo' : 'Inactivo';

                                return (
                                    <div key={h.keylogin} className="cupon-assign__row">
                                        <div className="cupon-assign__rowmeta">
                                            <div className="cupon-assign__rowname">{name}</div>
                                            <div className="cupon-assign__rowsub">
                                                {h?.email ? <span style={{ marginLeft: 10 }}>{h.email}</span> : null}
                                                <span style={{ marginLeft: 10, fontWeight: 700, opacity: 0.85 }}>
                                                    • {status}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            className="cupon-btn cupon-btn--ghost"
                                            type="button"
                                            onClick={() => handleUnassignFromHolder(h)}
                                            title="Remover cupón de este cliente"
                                        >
                                            Remover
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="cupon-assign__pager">
                            <button
                                className="cupon-btn cupon-btn--ghost"
                                type="button"
                                onClick={() => fetchHolders({ nextPage: Math.max(1, holdersPage - 1), nextQ: holdersQ })}
                                disabled={holdersLoading || holdersPage <= 1}
                            >
                                Anterior
                            </button>

                            <div className="cupon-pager__meta">
                                Página {holdersPage} de {holdersTotalPages}
                            </div>

                            <button
                                className="cupon-btn cupon-btn--ghost"
                                type="button"
                                onClick={() => fetchHolders({ nextPage: Math.min(holdersTotalPages, holdersPage + 1), nextQ: holdersQ })}
                                disabled={holdersLoading || holdersPage >= holdersTotalPages}
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>

                    <div className="cupon-assign__layout">
                        <div className="cupon-assign__panel">
                            <div className="cupon-assign__panelhead">
                                <div className="cupon-assign__paneltitle">Buscar clientes</div>
                                <div className="cupon-hint">Escribe un nombre y presiona Enter.</div>
                            </div>

                            <div className="cupon-assign__searchrow">
                                <div className="cupon-input-wrap">
                                    <span className="cupon-input-ico" aria-hidden="true">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" fill="none" stroke="currentColor" strokeWidth="2.2" />
                                            <path d="M16.5 16.5 21 21" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    <input
                                        className="cupon-input"
                                        placeholder="Ej: Juan Pérez"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') fetchClients({ nextPage: 1, nextSearch: e.target.value });
                                        }}
                                    />
                                </div>

                                <button
                                    className="cupon-btn cupon-btn--primary"
                                    type="button"
                                    onClick={() => fetchClients({ nextPage: 1, nextSearch: search })}
                                    disabled={loading}
                                >
                                    Buscar
                                </button>
                            </div>

                            <div className="cupon-assign__results">
                                {loading && <div className="cupon-assign__empty">Cargando...</div>}

                                {!loading && clients.length === 0 && (
                                    <div className="cupon-assign__empty">
                                        Sin resultados. Intenta con otro nombre.
                                    </div>
                                )}

                                {!loading && clients.map((acc) => {
                                    const name = getAccName(acc);
                                    const key = String(acc?.keylogin || '');
                                    const inSelected = alreadySelected(key);
                                    const hasCoupon = alreadyHasCoupon(key);

                                    const disabled = inSelected || hasCoupon;

                                    let label = 'Agregar';
                                    if (hasCoupon) label = 'Ya asignado';
                                    else if (inSelected) label = 'Agregado';

                                    return (
                                        <div key={acc.keylogin} className="cupon-assign__row">
                                            <div className="cupon-assign__rowmeta">
                                                <div className="cupon-assign__rowname">{name}</div>
                                                <div className="cupon-assign__rowsub">
                                                    {acc.email ? <span>{acc.email}</span> : null}
                                                    {hasCoupon ? (
                                                        <span style={{ marginLeft: 10, fontWeight: 700, opacity: 0.85 }}>
                                                            • Tiene cupón
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <button
                                                className={`cupon-btn ${disabled ? 'cupon-btn--ghost' : 'cupon-btn--primary'}`}
                                                type="button"
                                                disabled={disabled}
                                                onClick={() => addClient(acc)}
                                                title={
                                                    hasCoupon
                                                        ? 'Este cliente ya tiene el cupón (activo o inactivo). Remuévelo arriba si quieres reasignar.'
                                                        : (inSelected ? 'Ya agregado' : 'Agregar a por asignar')
                                                }
                                            >
                                                <span className="cupon-btn__icon" aria-hidden="true">
                                                    {disabled ? (
                                                        <svg viewBox="0 0 24 24">
                                                            <path d="M5 12l4 4L19 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    ) : (
                                                        <svg viewBox="0 0 24 24">
                                                            <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                                                        </svg>
                                                    )}
                                                </span>
                                                {label}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="cupon-assign__pager">
                                <button
                                    className="cupon-btn cupon-btn--ghost"
                                    type="button"
                                    onClick={() => fetchClients({ nextPage: Math.max(1, page - 1), nextSearch: search })}
                                    disabled={loading || page <= 1}
                                >
                                    Anterior
                                </button>

                                <div className="cupon-pager__meta">
                                    Página {page} de {totalPages}
                                </div>

                                <button
                                    className="cupon-btn cupon-btn--ghost"
                                    type="button"
                                    onClick={() => fetchClients({ nextPage: Math.min(totalPages, page + 1), nextSearch: search })}
                                    disabled={loading || page >= totalPages}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>

                        <div className="cupon-assign__panel">
                            <div className="cupon-assign__panelhead">
                                <div className="cupon-assign__paneltitle">Por asignar</div>
                                <div className="cupon-hint">
                                    Seleccionados: <b>{count}</b>
                                </div>
                            </div>

                            <div className="cupon-assign__chips">
                                {selected.length === 0 && (
                                    <div className="cupon-assign__empty">
                                        Sin clientes seleccionados.
                                    </div>
                                )}

                                {selected.map((s) => (
                                    <div key={s.keylogin} className="cupon-chip">
                                        <div className="cupon-chip__meta">
                                            <div className="cupon-chip__name">{s.nombre}</div>
                                        </div>

                                        <button
                                            className="cupon-chip__x"
                                            type="button"
                                            title="Quitar"
                                            onClick={() => removeClient(s.keylogin)}
                                        >
                                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="cupon-assign__actions">
                                <button className="cupon-btn cupon-btn--ghost" type="button" onClick={clearSelected} disabled={selected.length === 0}>
                                    Limpiar lista
                                </button>
                            </div>

                            {showRaw && (
                                <div className="cupon-assign__raw">
                                    <div className="cupon-label">Pegar keylogins</div>
                                    <textarea
                                        className="cupon-textarea"
                                        rows={3}
                                        placeholder="Uno por línea o separados por coma"
                                        value={keyloginsTxt}
                                        onChange={(e) => setKeyloginsTxt(e.target.value)}
                                    />
                                    <div className="cupon-assign__rawfoot">
                                        <div className="cupon-hint">Se agregan a “Por asignar”.</div>
                                        <button className="cupon-btn cupon-btn--primary" type="button" onClick={mergeRawKeylogins}>
                                            <span className="cupon-btn__icon" aria-hidden="true">
                                                <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
                                            </span>
                                            Agregar
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="cupon-field" style={{ marginTop: '12px' }}>
                                <div className="cupon-label">Nota</div>
                                <input
                                    className="cupon-input"
                                    value={nota}
                                    onChange={(e) => setNota(e.target.value)}
                                    placeholder="Ej: Cupón de compensación / Campaña X"
                                />
                            </div>

                            <form onSubmit={submit} className="cupon-modal__foot">
                                <button className="cupon-btn cupon-btn--ghost" type="button" onClick={close}>
                                    Cancelar
                                </button>
                                <button className="cupon-btn cupon-btn--primary" type="submit" disabled={count === 0}>
                                    <span className="cupon-btn__icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M19 8v6M22 11h-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                            <path d="M16 11a4 4 0 1 0-8 0" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                            <path d="M4 21a8 8 0 0 1 16 0" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    Asignar
                                </button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
